// web-commands.ts — the PER-APP half of the web shape: maps oriora_stash's Tauri data commands
// (normally served by ipc.rs/db.rs over native SQLite) to the shared browser WebStore. Plain CRUD
// (+ mark_used and the list_facets rollup) is a few lines each. The derived command
// (compute_signals) is computed on-device in Rust (signals.rs) — on web it returns a valid-but-
// empty shell so the recap UI never crashes; a faithful web build ports the signal logic to JS
// (the known per-app wrinkle, see web docs).

import type { CommandHandler, WebStore } from "./web-runtime";
import { computeSignalsWeb } from "./signals-web";
import type { Item } from "./store";
import { browserSearch, browserFetch } from "./web-search-browser";

const TABLE = "items";

function uuid(): string {
  // crypto.randomUUID is available in every modern browser.
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.round(Math.random() * 1e9)}`;
}
function nowIso(): string {
  return new Date().toISOString();
}

export function makeWebCommands(db: WebStore): Record<string, CommandHandler> {
  return {

    // ── WEB-SURFACE RETRIEVAL (Pattern 7, web half) ─────────────────────────────
    // On native, `web_search`/`web_fetch` are Rust commands browsing on the DEVICE. In a browser
    // there is no Rust, and without these the chat's `maybeWebContext` silently returns nothing —
    // the customer gets an ungrounded answer where the phone would have cited sources.
    // Here the BROWSER does it, on the customer's own IP: Tier 1 sources it may read directly
    // (no middleman), then a keyless reader for the open web. Oriora runs no search.
    web_search: async (args: Record<string, unknown>) =>
      await browserSearch(String(args.query ?? ""), typeof args.limit === "number" ? args.limit : 5),
    web_fetch: async (args: Record<string, unknown>) =>
      await browserFetch(String(args.url ?? ""), typeof args.maxChars === "number" ? args.maxChars : 6000),

    list_items: ({ season, location, category, limit }) => {
      let rows = db.all(TABLE);
      if (season) rows = rows.filter((r) => String(r.season) === String(season));
      if (location) rows = rows.filter((r) => String(r.location) === String(location));
      if (category) rows = rows.filter((r) => String(r.category) === String(category));
      rows.sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));
      return typeof limit === "number" ? rows.slice(0, limit) : rows;
    },
    get_item: ({ id }) => db.get(TABLE, String(id)),
    create_item: ({ input }) => {
      const now = nowIso();
      const base = {
        id: uuid(),
        name: "",
        category: "",
        season: "",
        occasion: "",
        location: "",
        quantity: 1,
        photo_ref: "",
        last_used_on: null,
        notes: "",
        created_at: now,
        updated_at: now,
      };
      // Spreading raw input would let an explicitly-passed `undefined` (e.g. `category: x || undefined`
      // from the file-a-box flow) CLOBBER the "" defaults above — saved rows then crash any render
      // that assumes string fields (measured live 2026-08-19: ErrorBoundary on "Add 20 items").
      const given = Object.fromEntries(Object.entries(input as Record<string, unknown>).filter(([, v]) => v !== undefined));
      const row = { ...base, ...given };
      return db.insert(TABLE, row as { id: string } & Record<string, unknown>);
    },
    update_item: ({ id, patch }) => {
      db.update(TABLE, String(id), { ...(patch as Record<string, unknown>), updated_at: nowIso() });
      return null;
    },
    delete_item: ({ id }) => {
      db.remove(TABLE, String(id));
      return null;
    },
    mark_used: ({ id, date }) => {
      db.update(TABLE, String(id), { last_used_on: String(date), updated_at: nowIso() });
      return null;
    },
    // Distinct present tokens — drives the filter chips (a plain rollup, not a signal).
    list_facets: () => {
      const rows = db.all(TABLE);
      const distinct = (key: string) =>
        [...new Set(rows.map((r) => String(r[key] ?? "").trim()).filter(Boolean))].sort();
      return { seasons: distinct("season"), locations: distinct("location"), categories: distinct("category") };
    },
    // Derived aggregate — Rust in signals.rs, faithfully ported for web in signals-web.ts.
    // This used to be a valid-but-EMPTY shell, so on web the season recap and Ask (both BILLED)
    // were handed a catalogue of zeros.
    // ⚠️ The parameter is `focusSeason` (store.ts:173), not `season` — naming it wrong here would
    // silently drop the focus and return whole-catalogue facts for a per-season question.
    compute_signals: ({ today, focusSeason }) => computeSignalsWeb(
      db.all(TABLE) as unknown as Item[],
      String(today ?? new Date().toISOString().slice(0, 10)),
      typeof focusSeason === "string" ? focusSeason : null,
    ),
  };
}
